import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Ban, Plus, Trash2, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const CancellationReasonsTab = ({ api }) => {
  const { user } = useAuth();
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchReasons = useCallback(async () => {
    try {
      const res = await api.get(`/tenants/${user.client_id}`);
      setReasons(res.data?.cancellation_reasons || []);
    } catch {
      toast.error('Failed to load cancellation reasons');
    } finally {
      setLoading(false);
    }
  }, [api, user.client_id]);

  useEffect(() => { fetchReasons(); }, [fetchReasons]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleaned = reasons.map(r => (r || '').trim()).filter(Boolean);
      await api.put(`/tenants/${user.client_id}`, { cancellation_reasons: cleaned });
      setReasons(cleaned);
      toast.success('Cancellation reasons saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateReason = (idx, value) => {
    setReasons(prev => { const next = [...prev]; next[idx] = value; return next; });
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <Card className="card-industrial" data-testid="cancellation-reasons-tab">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Ban className="h-5 w-5 text-muted-foreground" />
          <div>
            <CardTitle className="text-base">Job Cancellation Reasons</CardTitle>
            <CardDescription>Customise the list of reasons users can pick from when cancelling a job. Leave empty to use the default list.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {reasons.map((r, idx) => (
          <div key={idx} className="flex items-center gap-2" data-testid={`cancellation-reason-row-${idx}`}>
            <Input
              value={r}
              onChange={(e) => updateReason(idx, e.target.value)}
              placeholder="Enter cancellation reason"
              className="max-w-sm"
              data-testid={`cancellation-reason-input-${idx}`}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-red-500 hover:text-red-600"
              onClick={() => setReasons(prev => prev.filter((_, i) => i !== idx))}
              data-testid={`remove-cancellation-reason-${idx}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setReasons(prev => [...prev, ''])}
            data-testid="add-cancellation-reason-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add Reason
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-cancellation-reasons-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CancellationReasonsTab;
